import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { AnimatePresence } from 'framer-motion';
import { chakra, Flex, Text } from '@chakra-ui/react';
import useDelayedFunction from '@rsc/ui-components/hooks/useDelayedFunction';
import Link from '@rsc/ui-components/tailwind/Link';
import ActiveIndicator from '../../atoms/ActiveIndicator';
import NavBarContent from '../../organisms/NavBar/NavBarContent';

function DesktopNav({ menuItems, atTopOfPage, bannerShowing, ...props }) {
  const navRef = useRef(null);
  const [activeItem, setActiveItem] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);

  const handleChange = item => {
    setActiveItem(item);
  };

  const { handleMouseEnter, handleMouseLeave } =
    useDelayedFunction(handleChange);

  const reset = () => {
    setActiveItem(null);
    setHoveredItem(null);
  };

  const handleHoverIn = item => {
    handleMouseEnter(item);
    setHoveredItem(item);
    setIsExpanded(true);
  };

  const handleHoverOut = () => {
    handleMouseLeave();
    setHoveredItem(null);
    setIsExpanded(false);
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (!navRef.current?.contains(document.activeElement)) {
        reset();
      }
    }, 100);
  };

  return (
    <Flex
      id="desktop_nav"
      ref={navRef}
      as="nav"
      h="5rem"
      gap=".5rem"
      align="center"
      mr="auto"
      p={0}
      px={8}
      onBlur={handleBlur}
      {...props}
    >
      <chakra.a
        href="#main"
        className="skip-to-main-content-link"
        pos="absolute"
        right="120vw"
      >
        Skip to main content
      </chakra.a>

      {menuItems.map(item => {
        const isActive = activeItem?._key === item._key;
        const isHovered = hoveredItem?._key === item._key;
        const isStatic = item.menuType === 'static';
        return isStatic ? (
            <Flex
                    key={item._key}
                    as="button"
                    cursor="default"
                    pos="relative"
                    h="full"
                    align="center"
                    px={2}
                    whiteSpace="nowrap"
                    onMouseEnter={() => handleHoverIn(item)}
                    onMouseLeave={() => handleHoverOut()}
                    onKeyDown={e => {
                      if (['Enter', 'Space', 'ArrowDown'].includes(e.code)) {
                        e.stopPropagation();
                        e.preventDefault();
                        handleHoverIn(item);
                      }
                      if (['Escape'].includes(e.code)) {
                        e.stopPropagation();
                        e.preventDefault();
                        setHoveredItem(null);
                        setActiveItem(null);
                      }
                    }}
                  >
            <Text color="typography.lighter" mb="0">
            <Link key={item.title} link={item?.link}>{item.title}</Link>
          </Text>
          <ActiveIndicator
                      isActive={isActive || isHovered}
                      bg="brand.sunny"
                    /></Flex>
          ) : (
          <Flex
            key={item._key}
            as="button"
            cursor="default"
            pos="relative"
            h="full"
            align="center"
            px={2}
            whiteSpace="nowrap"
            onMouseEnter={() => handleHoverIn(item)}
            onMouseLeave={() => handleHoverOut()}
            onKeyDown={e => {
              if (['Enter', 'Space', 'ArrowDown'].includes(e.code)) {
                e.stopPropagation();
                e.preventDefault();
                handleHoverIn(item);
              }
              if (['Escape'].includes(e.code)) {
                e.stopPropagation();
                e.preventDefault();
                setHoveredItem(null);
                setActiveItem(null);
              }
            }}
          >
            <Text color="typography.lighter" mb="0">
              {item.title}
            </Text>
            <ActiveIndicator
              isActive={isActive || isHovered}
              bg="brand.sunny"
            />

            <AnimatePresence>
              {activeItem?._key === item._key && (
                <NavBarContent
                  activeItem={activeItem}
                  isExpanded={isExpanded}
                  atTopOfPage={atTopOfPage}
                  bannerShowing={bannerShowing}
                />
              )}
            </AnimatePresence>
          </Flex>
        );
      })}
    </Flex>
  );
}

DesktopNav.propTypes = {
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      menuType: PropTypes.string,
      _key: PropTypes.string,
    })
  ).isRequired,
  atTopOfPage: PropTypes.bool.isRequired,
  bannerShowing: PropTypes.bool.isRequired,
};

export default DesktopNav;
